import React, { useEffect, useState } from 'react';
import { deviceTrustAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, MonitorSmartphone, Cpu, Shield } from 'lucide-react';
import { getStoredFingerprint } from '../lib/deviceFingerprint';

const STATUS_VARIANT = { approved: 'success', pending: 'warning', blocked: 'destructive', revoked: 'secondary' };

const MyDevices = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const currentFp = getStoredFingerprint();

  useEffect(() => {
    (async () => {
      try {
        const [r, c] = await Promise.all([deviceTrustAPI.mine(), deviceTrustAPI.configSelf()]);
        setRows(r.data || []);
        setConfig(c.data || null);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Shield size={22} />My Devices</h1>
        <p className="text-muted-foreground text-sm">Devices you have used to access Pride Global HRMS.</p>
      </div>
      {config && (
        <div className="text-xs text-muted-foreground">
          Enforcement: <span className="font-medium">{config.enabled ? 'On' : 'Off'}</span>
          {config.enabled ? <> · TOFU: <span className="font-medium">{config.tofu_enabled ? 'On' : 'Off'}</span></> : null}
          {' '}· Max devices: <span className="font-medium">{config.max_devices_per_user || 5}</span>
        </div>
      )}
      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No devices registered yet.</CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="my-devices-list">
          {rows.map((d) => (
            <Card key={d.id}>
              <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                {d.source === 'agent' ? <Cpu size={18} className="text-muted-foreground" /> : <MonitorSmartphone size={18} className="text-muted-foreground" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{d.label || d.hostname || 'Unnamed device'}</span>
                    <Badge variant={STATUS_VARIANT[d.status] || 'secondary'}>{d.status}</Badge>
                    <Badge variant="outline" className="text-xs">{d.source}</Badge>
                    {currentFp && d.fingerprint === currentFp && (
                      <Badge variant="default" className="text-xs" data-testid="this-device-badge">This device</Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {d.platform || d.os || '—'} · {d.timezone || '—'} · last seen {d.last_seen_at}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono truncate">FP: {(d.fingerprint || '').slice(0, 32)}…</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyDevices;
